import { useEffect, useMemo, useState } from "react";
import { Save, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import BaseApi from "../../services/base-api";
import { PurchaseReturnsApi } from "../../services/purchase-returns-api";
import { useToast } from "../../context/ToastContext";
import Price from "../Currency/Price";
import {
  PurchaseReturn,
  ReturnableItem,
} from "../../types/PurchaseReturn";
import { getItemDisplay, getPersonName, toNumber } from "./helpers";

interface PurchaseOrderOption {
  id: string;
  poNumber?: string;
  orderNumber?: string;
  status: string;
  createdAt?: string;
  supplier?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}

interface FormRow {
  item: ReturnableItem;
  // Upper bound for the quantity input; see getRowCap
  cap: number;
  selected: boolean;
  quantity: number;
  reason: string;
}

interface PurchaseReturnFormModalProps {
  onClose: () => void;
  onSaved: (saved?: PurchaseReturn) => void;
  /** Pre-select a purchase order (e.g. coming from the PO view modal) */
  initialPurchaseOrderId?: string;
  /** When set the modal edits this (pending) return instead of creating one */
  editingReturn?: PurchaseReturn | null;
}

// Only POs that may have received stock can be returned against
const RETURNABLE_PO_STATUSES = ["approved", "delivered"];

// Rendered only while open, so state initialises from props on mount
const PurchaseReturnFormModal = ({
  onClose,
  onSaved,
  initialPurchaseOrderId,
  editingReturn,
}: PurchaseReturnFormModalProps) => {
  const { t, i18n } = useTranslation();
  const { success, err } = useToast();
  const isEditing = !!editingReturn;
  const showPicker = !isEditing && !initialPurchaseOrderId;

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderOption[]>(
    [],
  );
  const [purchaseOrderId, setPurchaseOrderId] = useState(
    editingReturn?.purchaseOrderId || initialPurchaseOrderId || "",
  );
  const [poNumber, setPoNumber] = useState(
    editingReturn?.purchaseOrder?.poNumber || "",
  );
  const [rows, setRows] = useState<FormRow[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [reason, setReason] = useState(editingReturn?.reason || "");
  const [notes, setNotes] = useState(editingReturn?.notes || "");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({ purchaseOrderId: "", items: "" });

  useEffect(() => {
    if (showPicker) {
      fetchPurchaseOrderOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!purchaseOrderId) {
      setRows([]);
      return;
    }

    // Ignore responses for a PO the user has since switched away from
    let ignore = false;
    fetchReturnableItems(purchaseOrderId, () => ignore);
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrderId]);

  const fetchPurchaseOrderOptions = async () => {
    try {
      const responses = await Promise.all(
        RETURNABLE_PO_STATUSES.map((status) =>
          BaseApi.get(`/purchase-orders?status=${status}&limit=100`),
        ),
      );
      const merged: PurchaseOrderOption[] = responses.flatMap((res) =>
        res?.success && Array.isArray(res.data) ? res.data : [],
      );
      merged.sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime(),
      );
      setPurchaseOrders(merged);
    } catch (error) {
      console.error("Failed to fetch purchase orders:", error);
      err(t("Failed to load purchase orders"));
    }
  };

  // The returnable-items endpoint counts quantities held by every in-flight
  // return, including the one being edited, so add that return's own
  // quantity back when editing. The server re-validates on save.
  const getRowCap = (item: ReturnableItem) => {
    const ownQuantity =
      editingReturn?.items
        ?.filter((line) => line.purchaseOrderItemId === item.purchaseOrderItemId)
        .reduce((sum, line) => sum + toNumber(line.quantity), 0) || 0;
    return Math.max(0, toNumber(item.returnableQuantity) + ownQuantity);
  };

  const fetchReturnableItems = async (
    poId: string,
    isStale: () => boolean,
  ) => {
    setLoadingItems(true);
    try {
      const res = await PurchaseReturnsApi.getReturnableItems(poId);
      if (isStale()) return;
      if (res?.success && res.data) {
        setPoNumber(res.data.poNumber || "");
        setRows(
          (res.data.items || []).map((item) => {
            const existing = editingReturn?.items?.find(
              (line) => line.purchaseOrderItemId === item.purchaseOrderItemId,
            );
            return {
              item,
              cap: getRowCap(item),
              selected: !!existing,
              quantity: existing ? toNumber(existing.quantity) : 1,
              reason: existing?.reason || "",
            };
          }),
        );
      } else {
        setRows([]);
        err(res?.message || t("Failed to load returnable items"));
      }
    } catch (error) {
      console.error("Failed to fetch returnable items:", error);
      err(t("Failed to load returnable items"));
    } finally {
      if (!isStale()) setLoadingItems(false);
    }
  };

  const updateRow = (index: number, patch: Partial<FormRow>) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    );
    setErrors((prev) => ({ ...prev, items: "" }));
  };

  const updateRowQuantity = (index: number, value: string, cap: number) => {
    const parsed = parseInt(value, 10);
    const quantity = Number.isFinite(parsed)
      ? Math.min(Math.max(parsed, 0), cap)
      : 0;
    updateRow(index, { quantity });
  };

  const runningTotal = useMemo(
    () =>
      rows
        .filter((row) => row.selected)
        .reduce(
          (sum, row) => sum + row.quantity * toNumber(row.item.unitCost),
          0,
        ),
    [rows],
  );

  const selectedCount = rows.filter((row) => row.selected).length;

  const validateForm = () => {
    const newErrors = { purchaseOrderId: "", items: "" };
    const selectedRows = rows.filter((row) => row.selected);

    if (!purchaseOrderId) {
      newErrors.purchaseOrderId = t("Please select a purchase order");
    }
    if (selectedRows.length === 0) {
      newErrors.items = t("Please select at least one item to return");
    } else if (
      selectedRows.some((row) => row.quantity < 1 || row.quantity > row.cap)
    ) {
      newErrors.items = t(
        "Each selected item needs a quantity between 1 and its returnable quantity",
      );
    }

    setErrors(newErrors);
    return !newErrors.purchaseOrderId && !newErrors.items;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const items = rows
      .filter((row) => row.selected)
      .map((row) => ({
        purchaseOrderItemId: row.item.purchaseOrderItemId,
        quantity: row.quantity,
        ...(row.reason.trim() ? { reason: row.reason.trim() } : {}),
      }));

    setIsSaving(true);
    try {
      const res = isEditing
        ? await PurchaseReturnsApi.update(editingReturn!.id, {
            items,
            reason: reason.trim(),
            notes: notes.trim(),
          })
        : await PurchaseReturnsApi.create({
            purchaseOrderId,
            items,
            ...(reason.trim() ? { reason: reason.trim() } : {}),
            ...(notes.trim() ? { notes: notes.trim() } : {}),
          });

      if (res?.success) {
        success(
          isEditing
            ? t("Purchase return updated successfully")
            : t("Purchase return created successfully"),
        );
        onSaved(res.data);
      } else {
        // Server messages (e.g. quantity exceeds returnable) are user-facing
        err(res?.message || t("Failed to save purchase return"));
      }
    } catch (error) {
      console.error("Failed to save purchase return:", error);
      err(t("Failed to save purchase return"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-black px-6 py-5 border-b border-gray-200 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {isEditing
                  ? t("Edit Purchase Return")
                  : t("New Purchase Return")}
              </h2>
              {(editingReturn?.returnNumber || poNumber) && (
                <p className="text-sm text-gray-200 mt-1">
                  {editingReturn?.returnNumber
                    ? `${editingReturn.returnNumber} · `
                    : ""}
                  {poNumber && `${t("PO")} ${poNumber}`}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Purchase Order Selection */}
          {showPicker && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Purchase Order")} <span className="text-red-500">*</span>
              </label>
              <select
                value={purchaseOrderId}
                onChange={(e) => {
                  setPurchaseOrderId(e.target.value);
                  setErrors((prev) => ({ ...prev, purchaseOrderId: "" }));
                }}
                className={`w-full px-4 py-3 border-2 ${
                  errors.purchaseOrderId ? "border-red-500" : "border-gray-200"
                } rounded-lg focus:border-black focus:outline-none transition-colors`}
              >
                <option value="">{t("Select purchase order")}</option>
                {purchaseOrders.map((po) => (
                  <option key={po.id} value={po.id}>
                    {po.poNumber || po.orderNumber || po.id} —{" "}
                    {getPersonName(po.supplier, "")} ({t(
                      po.status.charAt(0).toUpperCase() + po.status.slice(1),
                    )})
                  </option>
                ))}
              </select>
              {errors.purchaseOrderId && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.purchaseOrderId}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {t("Only approved or delivered purchase orders are listed")}
              </p>
            </div>
          )}

          {/* Items Section */}
          {purchaseOrderId && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-semibold text-gray-700">
                  {t("Items to Return")} <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {selectedCount} {t("selected")}
                </span>
              </div>

              {errors.items && (
                <p className="mb-2 text-sm text-red-500">{errors.items}</p>
              )}

              <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 w-10" />
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">
                          {t("Product")}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">
                          {t("Ordered")}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">
                          {t("Received")}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">
                          {t("Already Returned")}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">
                          {t("Returnable")}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">
                          {t("Unit Cost")}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">
                          {t("Return Qty")}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">
                          {t("Line Reason")}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">
                          {t("Total")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {loadingItems ? (
                        <tr>
                          <td
                            colSpan={10}
                            className="px-4 py-8 text-center text-gray-500 text-sm"
                          >
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-4 h-4 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
                              {t("Loading...")}
                            </div>
                          </td>
                        </tr>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={10}
                            className="px-4 py-8 text-center text-gray-500 text-sm"
                          >
                            {t("No items found on this purchase order")}
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, index) => {
                          const display = getItemDisplay(
                            row.item.product,
                            row.item.variation,
                            i18n.language,
                          );
                          const disabled = row.cap <= 0;
                          const unitCost = toNumber(row.item.unitCost);

                          return (
                            <tr
                              key={row.item.purchaseOrderItemId}
                              className={
                                disabled
                                  ? "bg-gray-50 text-gray-400"
                                  : row.selected
                                    ? "bg-indigo-50/40"
                                    : "hover:bg-gray-50"
                              }
                              title={
                                disabled
                                  ? t("Nothing left to return for this item")
                                  : undefined
                              }
                            >
                              <td className="px-3 py-3">
                                <input
                                  type="checkbox"
                                  checked={row.selected}
                                  disabled={disabled}
                                  onChange={(e) =>
                                    updateRow(index, {
                                      selected: e.target.checked,
                                      quantity:
                                        row.quantity > 0 ? row.quantity : 1,
                                    })
                                  }
                                  className="w-4 h-4 accent-black disabled:cursor-not-allowed"
                                />
                              </td>
                              <td className="px-3 py-3 min-w-[180px]">
                                <div
                                  className={`font-medium ${disabled ? "" : "text-gray-900"}`}
                                >
                                  {display.name || t("Unnamed product")}
                                </div>
                                {display.variationLabel && (
                                  <div className="text-xs text-gray-500">
                                    {display.variationLabel}
                                  </div>
                                )}
                                {display.sku && (
                                  <div className="text-xs text-gray-400">
                                    SKU: {display.sku}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-3 text-sm">
                                {row.item.orderedQuantity}
                              </td>
                              <td className="px-3 py-3 text-sm">
                                {row.item.receivedQuantity}
                              </td>
                              <td className="px-3 py-3 text-sm">
                                {row.item.alreadyReturnedQuantity}
                              </td>
                              <td className="px-3 py-3 text-sm font-semibold">
                                {row.cap}
                              </td>
                              <td className="px-3 py-3 text-sm">
                                <Price amount={unitCost} />
                              </td>
                              <td className="px-3 py-3">
                                <input
                                  type="number"
                                  min={1}
                                  max={row.cap}
                                  step={1}
                                  value={row.selected ? row.quantity : ""}
                                  disabled={disabled || !row.selected}
                                  onChange={(e) =>
                                    updateRowQuantity(
                                      index,
                                      e.target.value,
                                      row.cap,
                                    )
                                  }
                                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                />
                              </td>
                              <td className="px-3 py-3">
                                <input
                                  type="text"
                                  value={row.reason}
                                  disabled={disabled || !row.selected}
                                  onChange={(e) =>
                                    updateRow(index, { reason: e.target.value })
                                  }
                                  placeholder={t("Optional")}
                                  className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                />
                              </td>
                              <td className="px-3 py-3 text-sm font-semibold">
                                <Price
                                  amount={
                                    row.selected ? row.quantity * unitCost : 0
                                  }
                                />
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("Reason")}
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("e.g. Damaged on arrival, wrong item shipped")}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("Notes")}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none resize-none"
              placeholder={t("Add any additional notes...")}
            />
          </div>

          {/* Running Total */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-gray-700">
                {t("Estimated Return Total")}:
              </span>
              <span className="text-2xl font-bold text-black">
                <Price amount={runningTotal} />
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t("Final totals are calculated by the server when saved")}
            </p>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t-2 border-gray-200">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-semibold"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || loadingItems || !purchaseOrderId}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {isSaving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t("Saving...")}</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>
                    {isEditing ? t("Update Return") : t("Create Return")}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseReturnFormModal;
